import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, MessageSquare, Clock, CheckCircle, Users, ExternalLink, FileText } from 'lucide-react';
import SEO from '../components/SEO';
import { COMPLIANCE_DISCLOSURE_TEXT } from '../constants/site';

const Complaints = () => {
  return (
    <div className="min-h-screen pt-20">
      <SEO 
        title="Complaints Handling | Internal Dispute Resolution"
        description="Flash Funding's Internal Dispute Resolution (IDR) process. Learn how to lodge a complaint and our response timeframes. Free IDR service. AFCA member."
        keywords="Flash Funding complaints, dispute resolution, IDR process, AFCA, financial complaints, customer complaints, complaint handling"
        canonical="/complaints"
      />
      {/* Hero Section */}
      <section className="py-16 lg:py-20 bg-navy-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="font-heading font-black text-4xl sm:text-5xl text-white uppercase tracking-tight leading-none mb-4">
              Complaints<br />
              <span className="gold-gradient-text">Handling</span>
            </h1>
            <p className="font-body text-lg text-white/70">
              Internal Dispute Resolution (IDR) Process
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 lg:py-24 bg-navy-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-8">
            {/* Introduction */}
            <div className="bg-navy-700 border border-gold-500/20 p-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 rounded-lg bg-gold-500/10 flex items-center justify-center flex-shrink-0">
                  <FileText size={24} className="text-gold-500" />
                </div>
                <div>
                  <h2 className="font-heading font-bold text-2xl text-white uppercase tracking-tight">
                    Internal Dispute Resolution (IDR) Process
                  </h2>
                </div>
              </div>
              <div className="space-y-4 font-body text-white/70 leading-relaxed">
                <p>
                  This policy provides information about our internal dispute resolution (IDR) process. <strong className="text-gold-500">Our IDR service is provided to you free of charge.</strong>
                </p>
                <p className="text-white/50 text-sm border-l-2 border-gold-500/50 pl-4">
                  {COMPLIANCE_DISCLOSURE_TEXT}
                </p>
                <p>
                  We believe that it is essential for us to have the ability, authority and proper training to hear and respond appropriately to any complaints or disputes raised by our customers.
                </p>
              </div>
            </div>

            {/* How to Lodge a Complaint */}
            <div className="bg-navy-700 border border-white/5 p-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 rounded-lg bg-gold-500/10 flex items-center justify-center flex-shrink-0">
                  <MessageSquare size={24} className="text-gold-500" />
                </div>
                <div>
                  <h2 className="font-heading font-bold text-2xl text-white uppercase tracking-tight">
                    How You May Lodge a Complaint
                  </h2>
                </div>
              </div>
              <div className="space-y-6 font-body text-white/70 leading-relaxed">
                <p>You can lodge complaints by contacting:</p>
                
                {/* Contact Card */}
                <div className="bg-navy-900 border border-white/10 p-6 rounded-lg">
                  <p className="font-heading font-bold text-white text-lg mb-4">Mr Andrew Sunna | Director</p>
                  <p className="text-gold-500 font-semibold mb-4">Flash Funding</p>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <MapPin size={18} className="text-gold-500 mt-1 flex-shrink-0" />
                      <span>Unit 4, 38 Victoria Street<br />SMITHFIELD NSW 2164</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <Phone size={18} className="text-gold-500 flex-shrink-0" />
                      <a href="tel:0452223827" className="hover:text-gold-500 transition-colors font-mono">0452 223 827</a>
                    </li>
                    <li className="flex items-center gap-3">
                      <Mail size={18} className="text-gold-500 flex-shrink-0" />
                      <a href="mailto:hello@flashfunding.loan" className="hover:text-gold-500 transition-colors">hello@flashfunding.loan</a>
                    </li>
                  </ul>
                </div>

                <p>
                  You may also lodge a complaint by speaking to any representative of our business who will refer you to the Complaints Officer. You should explain the details of your complaint as clearly as you can. You may do this verbally or in writing.
                </p>
              </div>
            </div>

            {/* Additional Assistance */}
            <div className="bg-navy-700 border border-white/5 p-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 rounded-lg bg-gold-500/10 flex items-center justify-center flex-shrink-0">
                  <Users size={24} className="text-gold-500" />
                </div>
                <div>
                  <h2 className="font-heading font-bold text-2xl text-white uppercase tracking-tight">
                    Additional Assistance
                  </h2>
                </div>
              </div>
              <div className="space-y-4 font-body text-white/70 leading-relaxed">
                <p>To assist complainants who might need additional assistance to lodge a complaint, we:</p>
                <ul className="space-y-3 ml-4">
                  <li className="flex items-start gap-3">
                    <CheckCircle size={18} className="text-gold-500 mt-1 flex-shrink-0" />
                    <span>Offer multiple methods for lodging complaints, including phone, email, letter, social media, in person, or online</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle size={18} className="text-gold-500 mt-1 flex-shrink-0" />
                    <span>Do not require complaints to be in writing</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle size={18} className="text-gold-500 mt-1 flex-shrink-0" />
                    <span>Ensure that information provided to the public about our IDR process, including this policy, is available in a range of languages and formats (including large print and audiotape)</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle size={18} className="text-gold-500 mt-1 flex-shrink-0" />
                    <span>Provide training to all staff (not just complaints management staff) to enable staff to be able to identify, support and assist complainants who need additional assistance, including cross-cultural training</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle size={18} className="text-gold-500 mt-1 flex-shrink-0" />
                    <span>Allow representatives to lodge complaints on behalf of complainants, including financial counsellors, legal representatives, family members and friends</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Dealing with Complaints */}
            <div className="bg-navy-700 border border-white/5 p-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 rounded-lg bg-gold-500/10 flex items-center justify-center flex-shrink-0">
                  <FileText size={24} className="text-gold-500" />
                </div>
                <div>
                  <h2 className="font-heading font-bold text-2xl text-white uppercase tracking-tight">
                    Dealing with Complaints
                  </h2>
                </div>
              </div>
              <div className="space-y-6 font-body text-white/70 leading-relaxed">
                <p>Our process for dealing with complaints is as follows:</p>
                
                <div className="space-y-4">
                  <div className="bg-navy-900 border-l-4 border-gold-500 p-4">
                    <h4 className="font-heading font-bold text-white uppercase mb-2">Acknowledgement</h4>
                    <p>We will acknowledge receipt of your complaint promptly – that is, within one business day of receiving it, or as soon as practicable.</p>
                  </div>
                  
                  <div className="bg-navy-900 border-l-4 border-gold-500 p-4">
                    <h4 className="font-heading font-bold text-white uppercase mb-2">Assessment and Investigation</h4>
                    <p>We will review your complaint carefully and promptly, taking such steps and reviewing such documents as reasonably necessary.</p>
                  </div>
                  
                  <div className="bg-navy-900 border-l-4 border-gold-500 p-4">
                    <h4 className="font-heading font-bold text-white uppercase mb-2">IDR Response</h4>
                    <p>We will provide an 'IDR response', which is a written communication that sets out the final outcome of your complaint through our IDR process and your right to take your complaint to AFCA if you are not satisfied with the IDR response. If we reject or partially reject your complaint, we will clearly set out the reasons for our decision.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Response Timeframes */}
            <div className="bg-navy-700 border border-white/5 p-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 rounded-lg bg-gold-500/10 flex items-center justify-center flex-shrink-0">
                  <Clock size={24} className="text-gold-500" />
                </div>
                <div>
                  <h2 className="font-heading font-bold text-2xl text-white uppercase tracking-tight">
                    Response Timeframes
                  </h2>
                </div>
              </div>
              <div className="space-y-6 font-body text-white/70 leading-relaxed">
                <p>
                  Generally, we will provide an IDR response to you <strong className="text-white">no later than 30 calendar days</strong> after receiving the complaint. However, for some specific types of credit-related complaints, the following response timeframes apply:
                </p>
                
                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <thead>
                      <tr className="border-b border-white/10">
                        <th className="py-3 pr-4 font-heading font-bold text-white uppercase text-sm">Complaint Type</th>
                        <th className="py-3 font-heading font-bold text-white uppercase text-sm">Timeframe</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-white/5">
                        <td className="py-3 pr-4">Credit-related complaints involving default notices</td>
                        <td className="py-3 text-gold-500 font-mono">21 calendar days</td>
                      </tr>
                      <tr className="border-b border-white/5">
                        <td className="py-3 pr-4">Credit-related complaints involving hardship notices or requests to postpone enforcement proceedings</td>
                        <td className="py-3 text-gold-500 font-mono">21 calendar days*</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                
                <p className="text-sm text-white/50">
                  *Exceptions apply if we do not have sufficient information to make a decision, or if we reach an agreement with you.
                </p>

                <div className="bg-navy-900 border border-white/10 p-6 rounded-lg mt-6">
                  <h4 className="font-heading font-bold text-white uppercase mb-3">Early Resolution</h4>
                  <p className="mb-4">
                    We do not need to provide an IDR response to you if we close your complaint by the end of the fifth business day after receipt because we have:
                  </p>
                  <ul className="space-y-2 ml-4">
                    <li className="flex items-start gap-2">
                      <span className="text-gold-500">•</span>
                      <span>Resolved the complaint to your satisfaction; or</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-gold-500">•</span>
                      <span>Given you an explanation and/or apology we can take no further action to reasonably address your complaint.</span>
                    </li>
                  </ul>
                  <p className="mt-4 text-white/50 text-sm">
                    However, we must provide a written IDR response for complaints closed by the end of the fifth business day after receipt if the complainant requests a written response, or if the complaint is about hardship.
                  </p>
                </div>
              </div>
            </div>

            {/* AFCA - External Dispute Resolution */}
            <div className="bg-navy-700 border border-gold-500/30 p-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 rounded-lg bg-gold-500/10 flex items-center justify-center flex-shrink-0">
                  <ExternalLink size={24} className="text-gold-500" />
                </div>
                <div>
                  <h2 className="font-heading font-bold text-2xl text-white uppercase tracking-tight">
                    External Dispute Resolution – AFCA
                  </h2>
                </div>
              </div>
              <div className="space-y-6 font-body text-white/70 leading-relaxed">
                <p>
                  If we do not reach agreement on your complaint, you may refer your complaint to the <strong className="text-white">Australian Financial Complaints Authority (AFCA)</strong>.
                </p>
                
                <div className="bg-navy-900 border border-gold-500/20 p-6 rounded-lg">
                  <h4 className="font-heading font-bold text-gold-500 text-lg mb-4">Contact AFCA</h4>
                  <ul className="space-y-3">
                    <li className="flex items-center gap-3">
                      <Phone size={18} className="text-gold-500 flex-shrink-0" />
                      <span>Phone: <a href="tel:1800931678" className="text-gold-500 hover:underline font-mono">1800 931 678</a></span>
                    </li>
                    <li className="flex items-center gap-3">
                      <Mail size={18} className="text-gold-500 flex-shrink-0" />
                      <span>Email: <a href="mailto:info@afca.org.au" className="text-gold-500 hover:underline">info@afca.org.au</a></span>
                    </li>
                    <li className="flex items-start gap-3">
                      <MapPin size={18} className="text-gold-500 mt-1 flex-shrink-0" />
                      <span>Write to: GPO Box 3, Melbourne VIC 3001</span>
                    </li>
                  </ul>
                </div>

                <p className="text-white/50 text-sm italic">
                  The AFCA scheme is a free service established to provide you with an independent mechanism to resolve specific complaints.
                </p>
              </div>
            </div>
          </div>

          {/* Last Updated */}
          <p className="text-white/40 font-body text-sm text-center mt-12">
            Last updated: December 2025
          </p>
        </div>
      </section>
    </div>
  );
};

export default Complaints;
